import { BrandTagline } from "../components/BrandTagline";
import { Fragment, useState, useEffect, useRef } from "react";
import { useInfiniteQuery, useQuery, keepPreviousData } from "@tanstack/react-query";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FilmStrip, Television } from "@phosphor-icons/react";
import { Header } from "@/components/Header";
import { HomeFilterBar } from "@/components/HomeFilterBar";
import { RetroPlayer } from "@/components/RetroPlayer";
import { VideoCard } from "@/components/VideoCard";
import { SkeletonCard } from "@/components/SkeletonCard";
import { FeaturedHero } from "@/components/FeaturedHero";
import { GenreRow } from "@/components/GenreRow";
import { BecauseYouWatchedRow } from "@/components/BecauseYouWatchedRow";
import { ContinueWatchingRow } from "@/components/ContinueWatchingRow";
import { NewThisWeekRow } from "@/components/NewThisWeekRow";
import { WatchingNowRow } from "@/components/WatchingNowRow";
import { AdSlot } from "@/components/AdSlot";
import { TvModeToggle } from "@/components/TvModeToggle";
import { fetchVideos, fetchHero, fetchTrending } from "@/lib/api";
import { CategoryRows, CategorySpotlight, CATEGORY_ROW_TAGS, FEATURE_ONLY_TAGS } from "@/components/CategoryRows";

const LIMIT = 12;
const HOME_ROWS = [
  { key: "primetime-movies", title: "Primetime Movies For You", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "watched", content_type: "movie" }, action: { sort: "watched" } },
  { key: "papi", title: "Portfolio Papi 🎤", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "papi" }, action: { search: "papi" } },
  { key: "black-cinema", title: "Black Cinema 🎥", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "black_cinema" }, action: { search: "black_cinema" } },
  { key: "blaxploitation", title: "Blaxploitation", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "blaxploitation" }, action: { search: "blaxploitation" } },
  { key: "comedy-skits", title: "Comedy 😂", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "comedy", mix: true }, action: { search: "comedy" } },
  { key: "sitcoms", title: "Sitcoms & Comedy Series 📺", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "sitcom" }, action: { search: "sitcom" } },
  { key: "romance", title: "Romance ❤️", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "romance", mix: true }, action: { search: "romance" } },
  { key: "knicks", title: "Knicks Court: New York Knicks Highlights", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "recent", tag: "knicks" }, action: { search: "knicks" } },
  { key: "sports", title: "Catch Up: Latest Highlights", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "recent", tag: "sports_highlight" }, action: { search: "sports" } },
  { key: "video-podcasts", title: "Video Podcasts 🎙️", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "podcasts" }, action: { search: "podcasts" } },
  { key: "nature", title: "Nature 🏕️", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "nature" }, action: { search: "nature" } },
  { key: "anime", title: "Anime — Movies & Shows 🍥", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "anime" }, action: { search: "anime" } },
  { key: "retro-cartoons", title: "Retro Cartoons 📺", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "retro_cartoons" }, action: { search: "retro_cartoons" } },
  { key: "adult-animation", title: "Adult Animation 🌙", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "adult_animation" }, action: { search: "adult_animation" } },
  { key: "retro-toons", title: "90s Classics 📺", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "retro_toons" }, action: { search: "retro_toons" } },
  { key: "rap-rnb", title: "Hip-Hop & R&B 🎤", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", tag: "rap_rnb" }, action: { search: "rap_rnb" } },
  { key: "music", title: "New School Rap & R&B 🎶", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", content_type: "music" }, action: { search: "music" } },
  { key: "podcast", title: "Podcasts & Interviews", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "top_rated", content_type: "podcast" }, action: { search: "podcast" } },
  { key: "shows", title: "Primetime Shows For You", type: "curated", fetchArgs: { page: 1, limit: 12, sort: "watched", content_type: "show" }, action: { sort: "watched" } },
  { key: "action", title: "Action", type: "genre", tag: "action" },
  { key: "sci-fi", title: "Sci-Fi & Cyberpunk", type: "genre", tag: "sci-fi" },
  { key: "comedy", title: "Comedy", type: "genre", tag: "comedy" },
  { key: "drama", title: "Drama", type: "genre", tag: "drama" },
  { key: "thriller", title: "Thriller", type: "genre", tag: "thriller" },
  { key: "horror", title: "Horror Features", type: "genre", tag: "horror" },
  { key: "classic", title: "Classic Cinema", type: "genre", tag: "classic" },
];

const SORT_LABELS = { recent: "Most Recent", watched: "Top Watched" };

export default function Home() {
  const location = useLocation();
  const saved = JSON.parse(sessionStorage.getItem("rv_home_filters") || "{}");
  const { tag: routeTag } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState(routeTag ? routeTag.toLowerCase() : saved.search || "");
  const [year, setYear] = useState(saved.year || "All");
  const [sort, setSort] = useState(saved.sort || localStorage.getItem("rv_sort_pref") || "top_rated");
  const firstFilter = useRef(true);

  // Keep filters across Back navigation; snap to the top of fresh results on every change.
  useEffect(() => {
    sessionStorage.setItem("rv_home_filters", JSON.stringify({ search, year, sort }));
    localStorage.setItem("rv_sort_pref", sort);
    if (firstFilter.current) { firstFilter.current = false; return; }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [search, year, sort]);
  const [retroOpen, setRetroOpen] = useState(false);

  useEffect(() => {
    const kw = location.state?.keyword;
    const q = new URLSearchParams(location.search).get("q");
    if (kw) setSearch(kw);
    else if (q) setSearch(q);
  }, [location.state, location.search]);

  // Genre categories live at shareable /genre/<tag> URLs; Back to "/" clears the category.
  const prevTag = useRef(routeTag);
  useEffect(() => {
    if (routeTag) setSearch(routeTag.toLowerCase());
    else if (prevTag.current) setSearch((s) => (CATEGORY_ROW_TAGS.has(s.trim().toLowerCase()) ? "" : s));
    prevTag.current = routeTag;
  }, [routeTag]);
  useEffect(() => {
    const t = search.trim().toLowerCase();
    if (CATEGORY_ROW_TAGS.has(t)) {
      if (routeTag !== t) navigate(`/genre/${t}`);
    } else if (routeTag) {
      navigate("/", { replace: true });
    }
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only an explicit category/search flattens to a grid; sort & year reorder/filter inside each row.
  const filtering = !!search;
  const rowOverride = {
    ...(sort !== "top_rated" ? { sort } : {}),
    ...(year !== "All" ? { year } : {}),
  };

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPlaceholderData,
  } = useInfiniteQuery({
    queryKey: ["videos", search, year, sort],
    queryFn: ({ pageParam = 1 }) => fetchVideos({
      page: pageParam, limit: LIMIT, search, year, sort,
      content_type: FEATURE_ONLY_TAGS.has((search || "").trim().toLowerCase()) ? "feature" : "",
    }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.total_pages ? lastPage.page + 1 : undefined,
    placeholderData: keepPreviousData,
    enabled: filtering,
  });

  const handleSearch = (val) => setSearch(val);
  const catTag = (search || "").trim().toLowerCase();
  const isCategory = CATEGORY_ROW_TAGS.has(catTag);

  // Top-rated hero: ★7.5+ TMDb, 1,000+ votes, 60+ min features; free full films first, shuffled daily.
  const { data: heroData } = useQuery({
    queryKey: ["hero"],
    queryFn: fetchHero,
    staleTime: 10 * 60 * 1000,
  });
  const featuredSlides = heroData?.items ?? [];

  const videos = data?.pages.flatMap((p) => p.items) ?? [];
  const total = data?.pages?.[0]?.total ?? 0;

  const loaderRef = useRef(null);
  useEffect(() => {
    const el = loaderRef.current;
    if (!el || !hasNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "250px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, videos.length]);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header search={search} onSearch={handleSearch} />

      <HomeFilterBar
        genre={search}
        setGenre={setSearch}
        year={year}
        setYear={setYear}
        sort={sort}
        setSort={setSort}
      />

      <BrandTagline />

      {/* Retro Mode entry — stylized CRT channel-surf toggle */}
      <div className="border-b border-white/10 bg-[#070707]">
        <div className="mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-4 sm:px-8 py-2.5 flex items-center justify-between gap-4">
          <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
            Lean back · no clicking · endless cartoons &amp; classics
          </span>
          <button
            data-testid="retro-mode-toggle"
            onClick={() => setRetroOpen(true)}
            className="group relative ml-auto flex items-center gap-2.5 overflow-hidden rounded-full border border-amber/50 bg-gradient-to-r from-amber/15 to-crimson/10 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-amber hover:text-black hover:border-amber transition-colors duration-300"
          >
            <span className="absolute inset-0 -translate-x-full bg-amber transition-transform duration-300 group-hover:translate-x-0" />
            <Television size={17} weight="duotone" className="relative z-10" />
            <span className="relative z-10">Retro Mode</span>
            <span className="relative z-10 hidden sm:inline rounded-sm bg-crimson/80 px-1.5 py-0.5 text-[8px] text-white group-hover:bg-black/80">
              CRT
            </span>
          </button>
        </div>
      </div>

      {retroOpen && <RetroPlayer onClose={() => setRetroOpen(false)} />}


      {!filtering && (
        <div className="mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-5 sm:px-8 pt-8">
          <WatchingNowRow />
        </div>
      )}

      {!filtering && <FeaturedHero slides={featuredSlides} />}
      {isCategory && <CategorySpotlight tag={catTag} sort={sort} year={year} />}

      <main className="mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-5 sm:px-8 py-10">
        {!filtering ? (
          /* ---------- Genre-row homepage ---------- */
          <div data-testid="genre-rows">
            <ContinueWatchingRow />
            <NewThisWeekRow />
            <BecauseYouWatchedRow />
            {HOME_ROWS.map((row) => (
              <GenreRow
                key={row.key}
                title={row.title}
                tag={row.type === "genre" ? row.tag : row.key}
                fetchArgs={row.type === "curated" ? row.fetchArgs : undefined}
                override={rowOverride}
                sortLabel={SORT_LABELS[sort]}
                onViewAll={() => {
                  if (row.type === "genre") {
                    setSearch(row.tag);
                  } else if (row.action?.search) {
                    setSearch(row.action.search);
                  } else if (row.action?.sort) {
                    setSearch("");
                    setYear("All");
                    setSort(row.action.sort);
                  }
                }}
              />
            ))}
          </div>
        ) : (
          /* ---------- Filtered feed (with in-feed native ads) ---------- */
          <>
            {isCategory && <CategoryRows tag={catTag} sort={sort} year={year} />}
            <div className="flex items-baseline justify-between mb-6">
              <h2 data-testid="results-heading" className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">
                {isCategory ? `All ${catTag.replace(/_/g, " ")} titles` : search ? `Results for "${search}"` : "Browse All"}
              </h2>
              {total > 0 && <span className="font-mono text-xs text-white/30">{total} films</span>}
            </div>

            {isLoading || (isPlaceholderData && isFetching) ? (
              <div data-testid="search-skeleton" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 min-[1600px]:grid-cols-5 min-[2200px]:grid-cols-6 gap-6 lg:gap-8">
                {Array.from({ length: 8 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : videos.length === 0 ? (
              <div data-testid="empty-state">
                <div className="flex flex-col items-start py-14">
                  <FilmStrip size={48} weight="light" className="text-white/20 mb-5" />
                  <p data-testid="empty-state-title" className="font-serif text-3xl text-white/80">
                    {search ? `No results found for \u201c${search}\u201d` : "No films found"}
                  </p>
                  <p className="mt-2 text-white/40 text-sm">Check the spelling or try a title, actor or genre. Here&apos;s what everyone is watching:</p>
                </div>
                <GenreRow title="Trending Now" tag="empty-trending" queryFn={() => fetchTrending(15)} />
              </div>
            ) : (
              <>
                <div
                  className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 min-[1600px]:grid-cols-5 min-[2200px]:grid-cols-6 gap-6 lg:gap-8 transition-opacity duration-300 ${isFetching && !isFetchingNextPage ? "opacity-60" : "opacity-100"}`}
                  data-testid="video-grid"
                >
                  {videos.map((v, i) => (
                    <Fragment key={v.video_id}>
                      <VideoCard video={v} index={i} />
                      {(i + 1) % 8 === 0 && <AdSlot type="native_card" />}
                    </Fragment>
                  ))}
                </div>

                {isFetchingNextPage && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 min-[1600px]:grid-cols-5 min-[2200px]:grid-cols-6 gap-6 lg:gap-8 mt-6 lg:mt-8">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <SkeletonCard key={`n-${i}`} />
                    ))}
                  </div>
                )}

                <div ref={loaderRef} data-testid="infinite-loader" className="h-16" />

                {!hasNextPage && (
                  <p className="text-center font-mono text-[11px] uppercase tracking-[0.2em] text-white/25 mt-8">
                    End of the reel — {total} films
                  </p>
                )}
              </>
            )}
          </>
        )}
      </main>

      <footer className="border-t border-white/10 mt-10">
        <div className="mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-5 sm:px-8 py-8 flex flex-wrap items-center gap-4 justify-between">
          <span className="font-serif text-lg text-white/60">Reel<span className="text-amber">Vault</span></span>
          <div className="flex items-center gap-4">
            <TvModeToggle variant="footer" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/30">© 2026 Independent Media Archive</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
